import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getCourseCatalog, enrollInCourse } from '../../api/educentialApi';
import { BookOpen, User, Clock, Users, CheckCircle, AlertTriangle, TrendingUp, Sparkles, ArrowRight } from 'lucide-react';
import './CourseCatalog.css';

function CourseCatalog() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [topCourses, setTopCourses] = useState([]);
  const [recommendedCourses, setRecommendedCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrolling, setEnrolling] = useState(null);
  const [notification, setNotification] = useState(null);

  const [filters, setFilters] = useState({
    category: '',
    level: '',
    term: '',
    search: '',
  });

  useEffect(() => {
    loadCourses();
  }, [filters]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCourseCatalog(filters);
      if (response.success) {
        const allCourses = response.data;
        setCourses(allCourses);

        // Calculate top courses (by enrollment count)
        const sorted = [...allCourses].sort((a, b) =>
          (b._count?.Enrollments || 0) - (a._count?.Enrollments || 0)
        );
        setTopCourses(sorted.slice(0, 6)); // Top 6 courses

        // Calculate recommended courses (based on user level or popular courses)
        let recommended = [];
        if (user) {
          // Recommend courses that match any enrolled course's category or level
          // For now, just filter by similar characteristics
          recommended = allCourses.filter(course =>
            course.category !== filters.category &&
            (course._count?.Enrollments || 0) > 0
          ).slice(0, 6);
        }

        // If no personalized recommendations or not logged in, show popular courses
        if (recommended.length === 0) {
          recommended = sorted.filter(course => course.id !== sorted[0]?.id).slice(0, 6);
        }
        setRecommendedCourses(recommended);
      }
    } catch (err) {
      setError(err.message || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (courseId, courseTitle) => {
    // Check if user is logged in
    if (!user) {
      setNotification({
        type: 'error',
        message: 'Please login to enroll in courses'
      });
      setTimeout(() => {
        navigate('/login', { state: { from: '/courses/catalog' } });
      }, 1500);
      return;
    }

    try {
      setEnrolling(courseId);
      const response = await enrollInCourse(courseId);
      if (response.success) {
        setNotification({
          type: 'success',
          message: `Successfully enrolled in "${courseTitle}"!`
        });
        setTimeout(() => {
          navigate(`/courses/${courseId}/learn`);
        }, 1000);
      }
    } catch (err) {
      console.error('Enrollment error:', err);
      if (err.message.includes('Already enrolled')) {
        setNotification({
          type: 'info',
          message: 'You are already enrolled in this course'
        });
        setTimeout(() => {
          navigate(`/courses/${courseId}/learn`);
        }, 1500);
      } else if (err.response?.status === 401) {
        setNotification({
          type: 'error',
          message: 'Please login to enroll in courses'
        });
        setTimeout(() => {
          navigate('/login', { state: { from: '/courses/catalog' } });
        }, 1500);
      } else {
        setNotification({
          type: 'error',
          message: err.message || 'Failed to enroll. Please try again.'
        });
      }
    } finally {
      setEnrolling(null);
      // Auto-hide notification after 5 seconds
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const categories = [
    'Mathematics',
    'English',
    'Science',
    'Social Studies',
    'Arts',
    'Computer Science',
    'Physical Education',
  ];

  const levels = ['Primary', 'JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3'];
  const terms = ['First Term', 'Second Term', 'Third Term'];

  if (loading && courses.length === 0) {
    return (
      <div className="catalog-container">
        <div className="loading">Loading courses...</div>
      </div>
    );
  }

  return (
    <div className="catalog-container">
      {/* Notification Banner */}
      {notification && (
        <div className={`notification-banner ${notification.type}`}>
          <div className="notification-content">
            {notification.type === 'success' && <CheckCircle className="w-5 h-5" />}
            {notification.type === 'error' && <AlertTriangle className="w-5 h-5" />}
            {notification.type === 'info' && <BookOpen className="w-5 h-5" />}
            <span>{notification.message}</span>
          </div>
          <button onClick={() => setNotification(null)} className="notification-close">
            ×
          </button>
        </div>
      )}

      <div className="catalog-hero">
        <h1>Browse Courses</h1>
        <p>Explore our comprehensive K-12 curriculum aligned with WAEC, NECO, and JAMB standards</p>
      </div>

      {/* Filters Section */}
      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search courses..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="filter-select"
          >
            <option value="">All Subjects</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>

          <select
            value={filters.level}
            onChange={(e) => setFilters({ ...filters, level: e.target.value })}
            className="filter-select"
          >
            <option value="">All Levels</option>
            {levels.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>

          <select
            value={filters.term}
            onChange={(e) => setFilters({ ...filters, term: e.target.value })}
            className="filter-select"
          >
            <option value="">All Terms</option>
            {terms.map((term) => (
              <option key={term} value={term}>
                {term}
              </option>
            ))}
          </select>

          {(filters.category || filters.level || filters.term || filters.search) && (
            <button
              className="btn-clear-filters"
              onClick={() => setFilters({ category: '', level: '', term: '', search: '' })}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={loadCourses}>Retry</button>
        </div>
      )}

      {/* Top Courses Section - Only show when no filters are active */}
      {!filters.category && !filters.level && !filters.term && !filters.search && topCourses.length > 0 && (
        <div className="featured-section">
          <div className="section-header-with-icon">
            <div className="section-icon-wrapper trending">
              <TrendingUp className="section-icon" />
            </div>
            <div>
              <h2 className="section-title">Top Courses</h2>
              <p className="section-subtitle">Most popular courses among students</p>
            </div>
          </div>
          <div className="courses-grid">
            {topCourses.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                onEnroll={handleEnroll}
                enrolling={enrolling === course.id}
                onViewDetails={() => navigate(`/courses/${course.id}`)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Recommended Courses Section - Only show when no filters are active */}
      {!filters.category && !filters.level && !filters.term && !filters.search && recommendedCourses.length > 0 && (
        <div className="featured-section">
          <div className="section-header-with-icon">
            <div className="section-icon-wrapper recommended">
              <Sparkles className="section-icon" />
            </div>
            <div>
              <h2 className="section-title">Recommended for You</h2>
              <p className="section-subtitle">
                {user ? 'Courses picked based on your learning journey' : 'Featured courses to get you started'}
              </p>
            </div>
          </div>
          <div className="courses-grid">
            {recommendedCourses.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                onEnroll={handleEnroll}
                enrolling={enrolling === course.id}
                onViewDetails={() => navigate(`/courses/${course.id}`)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Results Count - Show when filters are active */}
      {(filters.category || filters.level || filters.term || filters.search) && (
        <div className="results-header">
          <h2>
            {courses.length} {courses.length === 1 ? 'Course' : 'Courses'} Available
          </h2>
        </div>
      )}

      {/* All Courses Section - Show when filters are active or as fallback */}
      {(filters.category || filters.level || filters.term || filters.search) && (
        <>
          {courses.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <BookOpen className="w-16 h-16 text-gray-400" />
              </div>
              <h3>No courses found</h3>
              <p>Try adjusting your filters or search terms</p>
            </div>
          ) : (
            <div className="courses-grid">
              {courses.map((course) => (
                <CourseCard
                  key={course.id}
                  course={course}
                  onEnroll={handleEnroll}
                  enrolling={enrolling === course.id}
                  onViewDetails={() => navigate(`/courses/${course.id}`)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Browse All Courses - Show when no filters are active */}
      {!filters.category && !filters.level && !filters.term && !filters.search && courses.length > 0 && (
        <div className="featured-section">
          <div className="section-header-with-icon">
            <div className="section-icon-wrapper all-courses">
              <BookOpen className="section-icon" />
            </div>
            <div>
              <h2 className="section-title">All Courses</h2>
              <p className="section-subtitle">Browse our complete K-12 curriculum catalog</p>
            </div>
          </div>
          <div className="courses-grid">
            {courses.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                onEnroll={handleEnroll}
                enrolling={enrolling === course.id}
                onViewDetails={() => navigate(`/courses/${course.id}`)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CourseCard({ course, onEnroll, enrolling, onViewDetails }) {
  const totalLessons =
    course.Modules?.reduce((sum, module) => sum + (module.Lessons?.length || 0), 0) || 0;

  return (
    <div className="course-card">
      <div className="course-thumbnail" onClick={onViewDetails}>
        {course.thumbnail ? (
          <img src={course.thumbnail} alt={course.title} />
        ) : (
          <div className="placeholder-thumbnail">
            <span>{course.category.charAt(0)}</span>
          </div>
        )}
        <div className="course-level-badge">{course.level}</div>
      </div>

      <div className="course-body">
        <div className="course-header" onClick={onViewDetails}>
          <h3 className="course-title">{course.title}</h3>
          <p className="course-category">{course.category}</p>
        </div>

        <p className="course-description">{course.description}</p>

        <div className="course-meta">
          <div className="meta-item">
            <User className="w-4 h-4" />
            <span>{course.Creator?.fullName || 'Educator'}</span>
          </div>
          <div className="meta-item">
            <BookOpen className="w-4 h-4" />
            <span>{totalLessons} lessons</span>
          </div>
          <div className="meta-item">
            <Clock className="w-4 h-4" />
            <span>{course.duration}h</span>
          </div>
          {course._count?.Enrollments > 0 && (
            <div className="meta-item">
              <Users className="w-4 h-4" />
              <span>{course._count.Enrollments} students</span>
            </div>
          )}
        </div>

        {course.term && <div className="course-term">{course.term}</div>}

        <div className="course-footer">
          <div className="course-price">
            {course.price && parseFloat(course.price) > 0 ? (
              <span className="price">₦{parseFloat(course.price).toLocaleString()}</span>
            ) : (
              <span className="price-free">Free</span>
            )}
          </div>

          <div className="course-actions">
            <button className="btn-view" onClick={onViewDetails}>
              View Details
            </button>
            <button
              className="btn-enroll"
              onClick={() => onEnroll(course.id, course.title)}
              disabled={enrolling}
            >
              {enrolling ? 'Enrolling...' : 'Enroll Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CourseCatalog;
